import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '../components/ui/Table';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { Plus, Edit2, Trash2, MapPin } from 'lucide-react';
import { listResorts, createResort, deleteResort, uploadResortFile } from '../services/resortAdminApi';
import { useAuth } from '../context/AuthContext';

interface Resort {
  name: string;
  resort_name: string;
  resort_image?: string;
  default_warehouse?: string;
}

export const Resorts: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'Resort Super Admin';
  const [searchTerm, setSearchTerm] = useState('');
  const [resorts, setResorts] = useState<Resort[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean; resort: Resort | null }>({ show: false, resort: null });
  const [formData, setFormData] = useState({
    resort_name: '',
  });
  const [resortImage, setResortImage] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load resorts from API
  const loadResorts = async () => {
    setLoading(true);
    try {
      const result = await listResorts(searchTerm || undefined, page, pageSize);
      if (result && result.data) {
        setResorts(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error loading resorts:', error);
      setError(error.message || 'Failed to load resorts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResorts();
  }, [page, pageSize, searchTerm]);

  const handleEdit = (resort: Resort) => {
    navigate(`/resorts/${encodeURIComponent(resort.name)}`);
  };

  const handleDelete = async () => {
    if (!showDeleteModal.resort) return;

    setDeleting(true);
    setError('');
    try {
      await deleteResort(showDeleteModal.resort.name);
      setSuccess(`Resort "${showDeleteModal.resort.resort_name}" has been deleted`);
      setShowDeleteModal({ show: false, resort: null });
      loadResorts();
    } catch (error: any) {
      setError(error.message || 'Failed to delete resort');
    } finally {
      setDeleting(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const result = await uploadResortFile(file);
      if (result && result.file_url) {
        setResortImage(result.file_url);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setCreating(true);

    try {
      await createResort({
        resort_name: formData.resort_name,
        resort_image: resortImage || undefined,
      });
      setSuccess('Resort created successfully');
      setShowCreateModal(false);
      setFormData({ resort_name: '' });
      setResortImage('');
      loadResorts();
    } catch (error: any) {
      setError(error.message || 'Failed to create resort');
    } finally {
      setCreating(false);
    }
  };

  const getImageUrl = (imageUrl: string | undefined) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;
    return `${base}${imageUrl}`;
  };

  // Table columns
  const columns = [
    {
      key: 'resort_image',
      header: 'Image',
      render: (value: string | undefined) => (
        value ? (
          <img
            src={getImageUrl(value)}
            alt="Resort"
            className="h-10 w-10 rounded-md object-cover"
          />
        ) : (
          <div className="h-10 w-10 rounded-md bg-gray-200 flex items-center justify-center">
            <MapPin className="h-5 w-5 text-gray-400" />
          </div>
        )
      )
    },
    {
      key: 'resort_name',
      header: 'Resort Name'
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_: any, row: Resort) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-indigo-600 hover:text-indigo-900"
            title="Edit"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setShowDeleteModal({ show: true, resort: row })}
              className="text-red-600 hover:text-red-900"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Resorts</h1>
          <p className="mt-1 text-sm text-gray-600">Manage resort locations</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Resort
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Search */}
      <SearchFilter
        initialValue={searchTerm}
        onSearch={setSearchTerm}
        onFilter={() => {}}
        placeholder="Search resorts..."
      />

      {/* Table */}
      <Table
        columns={columns}
        data={resorts}
        isLoading={loading}
        emptyMessage="No resorts found"
        pagination={{
          page,
          page_size: pageSize,
          total
        }}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Create Resort</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({ resort_name: '' });
                  setResortImage('');
                  setError('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Resort Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.resort_name}
                  onChange={(e) => setFormData({ ...formData, resort_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resort Image
                </label>
                {resortImage ? (
                  <div className="mt-2 space-y-2">
                    <div className="relative inline-block">
                      <img
                        src={getImageUrl(resortImage)}
                        alt="Resort"
                        className="h-32 w-32 object-cover rounded-md border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => setResortImage('')}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-sm font-bold"
                      >
                        <span className="sr-only">Remove</span>
                        ×
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <MapPin className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-alps-primary hover:text-alps-primary-dark">
                          <span>{uploadingImage ? 'Uploading...' : 'Upload an image'}</span>
                          <input
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={uploadingImage}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ resort_name: '' });
                    setResortImage('');
                    setError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !formData.resort_name}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal.show && showDeleteModal.resort && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-red-600">Delete Resort</h2>
              <button
                onClick={() => setShowDeleteModal({ show: false, resort: null })}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the resort "{showDeleteModal.resort.resort_name}"?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal({ show: false, resort: null })}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
